import { useEffect, useRef, useState, type ReactNode } from "react";
import { useInstall, useOnline, type InstallPlatform } from "@/lib/pwa";

const DISMISS_KEY = "ha-install-dismissed";

/** Guillemets français avec espaces insécables (pas de « » isolé en fin de ligne). */
const Q = ({ children }: { children: ReactNode }) => <>«{"\u00a0"}{children}{"\u00a0"}»</>;

const STEPS: Record<InstallPlatform, { title: string; steps: ReactNode[] }> = {
  ios: {
    title: "Sur iPhone ou iPad",
    steps: [
      <>Ouvrez Hardwood Atlas dans <strong>Safari</strong> (ou Chrome).</>,
      <>Touchez le bouton <strong>Partager</strong> (le carré avec une flèche vers le haut ; sur les versions récentes d'iOS, il se trouve dans le menu <strong>···</strong>).</>,
      <>Choisissez <strong><Q>Sur l'écran d'accueil</Q></strong>, puis <strong><Q>Ajouter</Q></strong>.</>,
    ],
  },
  android: {
    title: "Sur Android",
    steps: [
      <>Ouvrez le menu du navigateur (<strong>⋮</strong> en haut à droite dans Chrome).</>,
      <>Choisissez <strong><Q>Installer l'application</Q></strong> (ou <Q>Ajouter à l'écran d'accueil</Q>).</>,
    ],
  },
  "desktop-chromium": {
    title: "Sur ordinateur (Chrome, Edge)",
    steps: [
      <>Cliquez sur l'icône d'installation à droite de la barre d'adresse (un écran avec une flèche).</>,
      <>À défaut : menu <strong>⋮</strong> de Chrome → <Q>Caster, enregistrer et partager</Q> → <strong><Q>Installer la page en tant qu'application</Q></strong> ; dans Edge, menu <strong>···</strong> → <Q>Applications</Q> → <strong><Q>Installer ce site en tant qu'application</Q></strong>.</>,
    ],
  },
  "desktop-safari": {
    title: "Sur Mac (Safari)",
    steps: [<>Menu <strong>Fichier</strong> → <strong><Q>Ajouter au Dock</Q></strong> (macOS Sonoma ou plus récent).</>],
  },
  "desktop-firefox": {
    title: "Sur ordinateur (Firefox)",
    steps: [<>Firefox ne sait pas installer un site comme application sur ordinateur : ouvrez Hardwood Atlas dans <strong>Chrome</strong>, <strong>Edge</strong> ou <strong>Safari</strong> pour l'installer.</>],
  },
  other: {
    title: "Dans votre navigateur",
    steps: [<>Cherchez dans le menu du navigateur <Q>Installer l'application</Q>, <Q>Ajouter à l'écran d'accueil</Q> ou <Q>Ajouter au Dock</Q>.</>],
  },
};

const DownloadIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <rect x="5" y="2.5" width="14" height="19" rx="2.5" />
    <path d="M12 7v7M9 11.5l3 3 3-3M10 18.5h4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function InstallDialog({ open, onClose, platform }: { open: boolean; onClose: () => void; platform: InstallPlatform }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  const main = STEPS[platform];
  const others = (["ios", "android", "desktop-chromium", "desktop-safari"] as const).filter((p) => p !== platform);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => { if (e.target === ref.current) onClose(); }}
      aria-labelledby="install-title"
      className="m-0 mx-auto mt-[10vh] w-[min(520px,calc(100vw-24px))] max-w-none rounded-2xl border border-line-strong bg-surface p-0 text-ink shadow-2xl backdrop:bg-black/60 backdrop:backdrop-blur-sm"
    >
      <div className="flex items-start gap-4 border-b border-line p-5">
        <img src="/icons/icon-192.png" alt="" width={48} height={48} className="rounded-xl" />
        <div className="min-w-0 flex-1">
          <h2 id="install-title" className="font-display text-2xl font-extrabold uppercase leading-none">Installer l'application</h2>
          <p className="mt-1.5 text-sm text-muted">Hardwood Atlas s'ouvre alors depuis votre écran d'accueil, en plein écran, et se met à jour tout seul.</p>
        </div>
        <button type="button" onClick={onClose} className="rounded-md border border-line-strong px-2 py-0.5 font-mono text-[0.65rem] text-muted" aria-label="Fermer">Échap</button>
      </div>
      <div className="space-y-5 p-5 text-sm">
        <section>
          <h3 className="kicker mb-2 text-accent-text">{main.title}</h3>
          <ol className="list-decimal space-y-1.5 pl-5 text-ink-2">{main.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
        </section>
        <details className="rounded-lg border border-line">
          <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-muted">Autres appareils</summary>
          <div className="space-y-3 border-t border-line px-3 py-3">
            {others.map((p) => (
              <div key={p}>
                <div className="font-semibold text-ink">{STEPS[p].title}</div>
                <ol className="mt-1 list-decimal space-y-1 pl-5 text-muted">{STEPS[p].steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
              </div>
            ))}
          </div>
        </details>
        <p className="text-xs text-faint">Les données restent celles du site, chargées en direct : hors connexion, l'application s'ouvre mais n'affiche rien comme <Q>à jour</Q>. Sur iPhone, l'application installée garde sa propre session : l'espace propriétaire demande une nouvelle connexion la première fois.</p>
      </div>
    </dialog>
  );
}

/**
 * Bouton « Installer l'application » : invitation du navigateur quand elle existe (Chrome, Edge,
 * Android), sinon la marche à suivre (iPhone, Safari). Masqué une fois l'application installée.
 */
export function InstallButton({ variant, onDone }: { variant: "icon" | "row"; onDone?: () => void }) {
  const { canPrompt, installed, platform, prompt } = useInstall();
  const [help, setHelp] = useState(false);
  if (installed) return null;
  // En-tête d'ordinateur : seulement quand le navigateur propose vraiment l'installation
  if (variant === "icon" && !canPrompt) return null;
  const onClick = async () => {
    if (!(await prompt())) setHelp(true);
    else onDone?.();
  };
  return (
    <>
      {variant === "icon" ? (
        <button type="button" onClick={onClick} className="grid h-10 w-10 place-items-center rounded-lg border border-line-strong text-muted hover:text-ink" aria-label="Installer l'application" title="Installer l'application">
          <DownloadIcon />
        </button>
      ) : (
        <button type="button" onClick={onClick} className="flex w-full items-center gap-3 rounded-lg border border-line px-3 py-2.5 text-left text-sm font-semibold text-ink-2">
          <DownloadIcon size={20} />
          <span className="flex-1">Installer l'application</span>
          <span className="font-mono text-[0.65rem] uppercase tracking-wider text-faint">écran d'accueil</span>
        </button>
      )}
      <InstallDialog open={help} onClose={() => { setHelp(false); onDone?.(); }} platform={platform} />
    </>
  );
}

/** Invitation discrète sur mobile, une seule fois : « Plus tard » la masque définitivement. */
export function InstallInvite() {
  const { installed, platform, canPrompt } = useInstall();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  if (installed || dismissed || !(canPrompt || platform === "ios" || platform === "android")) return null;
  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* préférence non conservée */ }
  };
  return (
    <div className="mt-4 flex items-center gap-3 rounded-xl border border-line bg-surface px-3 py-2.5 md:hidden">
      <img src="/icons/icon-192.png" alt="" width={36} height={36} className="shrink-0 rounded-lg" />
      <p className="min-w-0 flex-1 text-sm leading-snug text-ink-2"><strong className="text-ink">Hardwood en application</strong> : sur votre écran d'accueil, en plein écran.</p>
      <div className="flex shrink-0 flex-col items-stretch gap-1">
        <InstallButtonCompact />
        <button type="button" onClick={dismiss} className="font-mono text-[0.62rem] uppercase tracking-wider text-faint">Plus tard</button>
      </div>
    </div>
  );
}

function InstallButtonCompact() {
  const { platform, prompt } = useInstall();
  const [help, setHelp] = useState(false);
  return (
    <>
      <button type="button" onClick={async () => { if (!(await prompt())) setHelp(true); }} className="rounded-lg bg-accent px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-accent-ink">Installer</button>
      <InstallDialog open={help} onClose={() => setHelp(false)} platform={platform} />
    </>
  );
}

/** Hors connexion : rien n'est présenté comme à jour. */
export function OfflineBanner() {
  const online = useOnline();
  if (online) return null;
  return (
    <div role="status" className="border-t border-warn/40 bg-warn/10">
      <div className="mx-auto max-w-[1400px] px-4 py-1.5 text-sm text-ink-2 sm:px-6 lg:px-8">
        <span className="kicker mr-2 text-warn">Hors connexion</span>
        Les pages restent consultables, mais les données ne sont pas actualisées et aucun score en direct n'est affiché.
      </div>
    </div>
  );
}
